import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import { getQuotation } from "../../core/api/quotationService";
import { fetchPatients } from "../../core/redux/slices/patientSlice";
import { fetchProcedures } from "../../core/redux/slices/procedureSlice";
import { createQuotationThunk, updateQuotationThunk } from "../../core/redux/slices/quotationSlice";

const EMPTY_LINE = { procedureId: "", procedureName: "", qty: 1, rate: 0, amount: 0 };

const QUOTATION_TYPE_OPTIONS = [
  { value: "Local", label: "Local" },
  { value: "Arab", label: "Arab" },
];

const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

const calcLine = (qty, rate) => Math.max(0, Number(qty || 0) * Number(rate || 0));
const fmt = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

const QuotationForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const patients = useSelector((s) => s.patients.list);
  const procedures = useSelector((s) => s.procedures.list);

  const [patientMode, setPatientMode] = useState("existing");
  const [header, setHeader] = useState({
    patientId: "",
    patientName: "",
    patientAge: "",
    patientGender: "",
    patientMobile: "",
    quoteType: "Local",
    remarks: "",
  });
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const patientOptions = patients.map((p) => ({
    value: p.patientID || p.id,
    label: `${p.patientName} — ${p.mobileNumber || p.mobile || ""} (OP: ${p.opNumber || "—"})`,
    patientName: p.patientName,
  }));
  const procedureOptions = procedures.map((p) => ({
    value: p.procedureID || p.id,
    label: p.procedureName,
    rateLocal: p.chargeNormal || 0,
    rateArab: p.chargeArab || 0,
  }));

  const rateFor = (opt, quoteType) => (quoteType === "Arab" ? opt?.rateArab : opt?.rateLocal) || 0;

  useEffect(() => {
    dispatch(fetchPatients());
    dispatch(fetchProcedures());
  }, [dispatch]);

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      getQuotation(id)
        .then((res) => {
          const q = res.data;
          const patientId = q.patientID ?? q.patientId ?? "";
          setPatientMode(patientId ? "existing" : "manual");
          setHeader({
            patientId,
            patientName: q.patientName || "",
            patientAge: q.patientAge ?? "",
            patientGender: q.patientGender || "",
            patientMobile: q.mobileNumber || q.patientMobile || "",
            quoteType: q.quoteType || "Local",
            remarks: q.remarks || "",
          });
          const qLines = q.lines || [];
          setLines(qLines.length ? qLines.map((l) => ({
            procedureId: l.procedureID ?? l.procedureId ?? "",
            procedureName: l.procedureName || "",
            qty: l.qty ?? l.quantity ?? 1,
            rate: l.rate ?? 0,
            amount: l.amount ?? 0,
          })) : [{ ...EMPTY_LINE }]);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const grandTotal = lines.reduce((sum, l) => sum + calcLine(l.qty, l.rate), 0);

  const updateLine = (idx, field, value) => {
    setLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      next[idx].amount = calcLine(next[idx].qty, next[idx].rate);
      return next;
    });
  };
  const selectProcedure = (idx, opt) => {
    setLines((prev) => {
      const next = [...prev];
      const rate = rateFor(opt, header.quoteType);
      next[idx] = { ...next[idx], procedureId: opt?.value || "", procedureName: opt?.label || "", rate };
      next[idx].amount = calcLine(next[idx].qty, next[idx].rate);
      return next;
    });
  };
  const addLine = () => setLines((prev) => [...prev, { ...EMPTY_LINE }]);
  const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));

  // Quotation type is a whole-quotation pricing tier, not a per-line choice — switching it
  // re-derives every line's rate from the procedure master so the total always reflects the
  // currently selected tier, instead of leaving stale rates from the previous tier in place.
  const changeQuotationType = (newType) => {
    setHeader((h) => ({ ...h, quoteType: newType }));
    setLines((prev) => prev.map((l) => {
      if (!l.procedureId) return l;
      const opt = procedureOptions.find((o) => o.value === l.procedureId);
      const rate = rateFor(opt, newType);
      return { ...l, rate, amount: calcLine(l.qty, rate) };
    }));
  };

  const switchPatientMode = (mode) => {
    setPatientMode(mode);
    setHeader((h) => ({
      ...h,
      patientId: "",
      patientName: "",
      patientAge: "",
      patientGender: "",
      patientMobile: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (patientMode === "existing" && !header.patientId) {
      Swal.fire("Required", "Please select a patient.", "warning");
      return;
    }
    if (patientMode === "manual" && (!header.patientName.trim() || !header.patientMobile.trim() || !header.patientAge || !header.patientGender)) {
      Swal.fire("Required", "Please enter the patient's name, age, mobile number and gender.", "warning");
      return;
    }
    const validLines = lines.filter((l) => l.procedureId);
    if (!validLines.length) {
      Swal.fire("Required", "Add at least one treatment.", "warning");
      return;
    }

    setSaving(true);
    const payload = {
      ...header,
      patientId: patientMode === "existing" ? header.patientId : null,
      lines: validLines.map((l) => ({ ...l, discount: 0 })),
    };
    try {
      if (isEdit) {
        await dispatch(updateQuotationThunk({ id, data: payload })).unwrap();
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Quotation updated", showConfirmButton: false, timer: 2000 });
      } else {
        await dispatch(createQuotationThunk(payload)).unwrap();
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Quotation saved", showConfirmButton: false, timer: 2000 });
      }
      navigate("/quotations");
    } catch (err) {
      const fallback = isEdit ? "Failed to update quotation." : "Failed to save quotation.";
      Swal.fire("Error", typeof err === "string" ? err : fallback, "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>{isEdit ? "Edit Quotation" : "New Treatment Quotation"}</h4>
            <h6>{isEdit ? "Update quotation details" : "Create a patient treatment quotation"}</h6>
          </div>
          <div className="page-btn d-flex gap-2">
            {isEdit && (
              <button type="button" className="btn btn-outline-primary" onClick={() => window.open(`/quotations/${id}/print`, "_blank")}>
                Print
              </button>
            )}
            <button className="btn btn-cancel" onClick={() => navigate("/quotations")}>Back to List</button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Patient Details</h5>
              {!isEdit && (
                <div className="btn-group btn-group-sm" role="group">
                  <button type="button" className={`btn ${patientMode === "existing" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => switchPatientMode("existing")}>Existing Patient</button>
                  <button type="button" className={`btn ${patientMode === "manual" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => switchPatientMode("manual")}>Walk-in / New Patient</button>
                </div>
              )}
            </div>
            <div className="card-body">
              <div className="row">
                {patientMode === "existing" ? (
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Patient <span className="text-danger">*</span></label>
                    <Select
                      classNamePrefix="react-select"
                      options={patientOptions}
                      onChange={(opt) => setHeader((h) => ({ ...h, patientId: opt?.value || "", patientName: opt?.patientName || "" }))}
                      value={patientOptions.find((o) => o.value === header.patientId) || null}
                      placeholder="Search by name or mobile..."
                      isClearable
                      isSearchable
                      isDisabled={isEdit}
                    />
                  </div>
                ) : (
                  <>
                    <div className="col-lg-4 mb-3">
                      <label className="form-label">Name <span className="text-danger">*</span></label>
                      <input className="form-control" value={header.patientName}
                        onChange={(e) => setHeader((h) => ({ ...h, patientName: e.target.value }))} placeholder="Patient name" />
                    </div>
                    <div className="col-lg-2 mb-3">
                      <label className="form-label">Age <span className="text-danger">*</span></label>
                      <input type="number" className="form-control" min={1} value={header.patientAge}
                        onChange={(e) => setHeader((h) => ({ ...h, patientAge: e.target.value }))} />
                    </div>
                    <div className="col-lg-3 mb-3">
                      <label className="form-label">Mobile <span className="text-danger">*</span></label>
                      <input className="form-control" value={header.patientMobile}
                        onChange={(e) => setHeader((h) => ({ ...h, patientMobile: e.target.value }))} placeholder="Mobile number" />
                    </div>
                    <div className="col-lg-3 mb-3">
                      <label className="form-label">Gender <span className="text-danger">*</span></label>
                      <Select
                        classNamePrefix="react-select"
                        options={GENDER_OPTIONS}
                        value={GENDER_OPTIONS.find((o) => o.value === header.patientGender) || null}
                        onChange={(opt) => setHeader((h) => ({ ...h, patientGender: opt?.value || "" }))}
                        placeholder="Select Gender"
                      />
                    </div>
                    <div className="col-lg-12">
                      <small className="text-muted">No patient record is created — these details are saved on the quotation itself.</small>
                    </div>
                  </>
                )}
                <div className="col-lg-4 mb-3">
                  <label className="form-label">Quotation Type</label>
                  <Select
                    classNamePrefix="react-select"
                    options={QUOTATION_TYPE_OPTIONS}
                    value={QUOTATION_TYPE_OPTIONS.find((o) => o.value === header.quoteType) || null}
                    onChange={(opt) => changeQuotationType(opt?.value || "Local")}
                  />
                </div>
                <div className="col-lg-8 mb-3">
                  <label className="form-label">Remarks</label>
                  <input className="form-control" value={header.remarks}
                    onChange={(e) => setHeader((h) => ({ ...h, remarks: e.target.value }))}
                    placeholder="Optional notes" />
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Treatments</h5>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={addLine}>+ Add Row</button>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: 220 }}>Treatment</th>
                      <th style={{ width: 80 }} className="text-center">Qty</th>
                      <th style={{ width: 130 }} className="text-end">Rate (₹)</th>
                      <th style={{ width: 140 }} className="text-end">Amount (₹)</th>
                      <th style={{ width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((row, idx) => (
                      <tr key={idx}>
                        <td>
                          <Select
                            classNamePrefix="react-select"
                            options={procedureOptions}
                            value={procedureOptions.find((o) => o.value === row.procedureId) || null}
                            onChange={(opt) => selectProcedure(idx, opt)}
                            placeholder="Select treatment"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                          />
                        </td>
                        <td>
                          <input type="number" className="form-control form-control-sm text-center" min={1}
                            value={row.qty} onChange={(e) => updateLine(idx, "qty", e.target.value)} />
                        </td>
                        <td>
                          <input type="number" className="form-control form-control-sm text-end" min={0} step="0.01"
                            value={row.rate} onChange={(e) => updateLine(idx, "rate", e.target.value)} />
                        </td>
                        <td className="text-end align-middle">
                          <strong>{fmt(calcLine(row.qty, row.rate))}</strong>
                        </td>
                        <td className="text-center align-middle">
                          {lines.length > 1 && (
                            <button type="button" className="btn btn-sm text-danger p-1" onClick={() => removeLine(idx)}>×</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-4 offset-lg-8">
              <div className="card mb-3">
                <div className="card-header"><h5 className="mb-0">Summary</h5></div>
                <div className="card-body">
                  <table className="table table-sm mb-0">
                    <tbody>
                      <tr className="table-light">
                        <td className="fw-bold">Grand Total</td>
                        <td className="text-end fw-bold">₹{fmt(grandTotal)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex gap-2 justify-content-end mb-4">
            <button type="button" className="btn btn-cancel" onClick={() => navigate("/quotations")}>Cancel</button>
            <button type="submit" className="btn btn-submit" disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Update Quotation" : "Save Quotation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuotationForm;
